import React, { useState, useEffect } from 'react';
import {
    Table,
    Card,
    Typography,
    message,
    Space,
    Button,
    Modal,
    Form,
    Input,
    InputNumber,
    Popconfirm,
    Tag,
    Select,
    Row,
    Col,
    Divider,
    Tooltip
} from 'antd';
import {
    DesktopOutlined,
    ReloadOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    TagsOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { ApiService } from '../services/api';
import { DeviceType, Tag as TagType } from '../types';

const { Title } = Typography;
const { Option } = Select;

const DeviceTypeManagement: React.FC = () => {
    const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
    const [loading, setLoading] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [tagModalOpen, setTagModalOpen] = useState(false);
    const [editingDeviceType, setEditingDeviceType] = useState<DeviceType | null>(null);
    const [currentDeviceType, setCurrentDeviceType] = useState<DeviceType | null>(null);
    const [deviceTypeTags, setDeviceTypeTags] = useState<TagType[]>([]);
    const [allTags, setAllTags] = useState<TagType[]>([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0
    });

    const [createForm] = Form.useForm();
    const [editForm] = Form.useForm();

    useEffect(() => {
        loadDeviceTypes();
        loadAllTags();
    }, [pagination.current, pagination.pageSize]);

    const loadDeviceTypes = async () => {
        setLoading(true);
        try {
            const response = await ApiService.getDeviceTypeList({
                page: pagination.current,
                page_size: pagination.pageSize
            });

            if (response.code === 200) {
                setDeviceTypes(response.data.device_types);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.pagination.total
                }));
            } else {
                message.error(response.message || '获取设备类型列表失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '获取设备类型列表失败');
        } finally {
            setLoading(false);
        }
    };

    const loadAllTags = async () => {
        try {
            const response = await ApiService.getTagList({ language: 'zh' });
            if (response.code === 200) {
                setAllTags(response.data.tags);
            }
        } catch (error: any) {
            console.error('获取标签列表失败:', error);
        }
    };

    const loadDeviceTypeTags = async (deviceTypeId: number) => {
        try {
            const response = await ApiService.getDeviceTypeTags({ device_type_id: deviceTypeId });
            if (response.code === 200) {
                setDeviceTypeTags(response.data.tags);
            } else {
                message.error(response.message || '获取设备类型标签失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '获取设备类型标签失败');
        }
    };

    const handleCreate = async (values: any) => {
        try {
            const response = await ApiService.createDeviceType(values);
            if (response.code === 200) {
                message.success('设备类型创建成功');
                setCreateModalOpen(false);
                createForm.resetFields();
                loadDeviceTypes();
            } else {
                message.error(response.message || '创建设备类型失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '创建设备类型失败');
        }
    };

    const handleEdit = async (values: any) => {
        if (!editingDeviceType) return;

        try {
            const response = await ApiService.updateDeviceType({
                id: editingDeviceType.id,
                ...values
            });

            if (response.code === 200) {
                message.success('设备类型更新成功');
                setEditModalOpen(false);
                setEditingDeviceType(null);
                editForm.resetFields();
                loadDeviceTypes();
            } else {
                message.error(response.message || '更新设备类型失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '更新设备类型失败');
        }
    };

    const handleDelete = async (deviceType: DeviceType) => {
        try {
            const response = await ApiService.deleteDeviceType({ id: deviceType.id });
            if (response.code === 200) {
                message.success('设备类型删除成功');
                loadDeviceTypes();
            } else {
                message.error(response.message || '删除设备类型失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '删除设备类型失败');
        }
    };

    const openEditModal = (deviceType: DeviceType) => {
        setEditingDeviceType(deviceType);
        editForm.setFieldsValue({
            type: deviceType.type,
            description: deviceType.description,
            width: deviceType.width,
            height: deviceType.height
        });
        setEditModalOpen(true);
    };

    const openTagModal = async (deviceType: DeviceType) => {
        setCurrentDeviceType(deviceType);
        setTagModalOpen(true);
        await loadDeviceTypeTags(deviceType.id);
    };

    const handleAddTag = async (tagId: number) => {
        if (!currentDeviceType) return;

        try {
            const response = await ApiService.addDeviceTypeTag({
                device_type_id: currentDeviceType.id,
                tag_id: tagId
            });

            if (response.code === 200) {
                message.success('标签添加成功');
                await loadDeviceTypeTags(currentDeviceType.id);
                // 更新本地设备类型数据
                await loadDeviceTypes();
            } else {
                message.error(response.message || '添加标签失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '添加标签失败');
        }
    };

    const handleRemoveTag = async (tagId: number) => {
        if (!currentDeviceType) return;

        try {
            const response = await ApiService.removeDeviceTypeTag({
                device_type_id: currentDeviceType.id,
                tag_id: tagId
            });

            if (response.code === 200) {
                message.success('标签移除成功');
                await loadDeviceTypeTags(currentDeviceType.id);
                // 更新本地设备类型数据
                await loadDeviceTypes();
            } else {
                message.error(response.message || '移除标签失败');
            }
        } catch (error: any) {
            message.error(error.response?.data?.message || '移除标签失败');
        }
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 80
        },
        {
            title: '设备类型号',
            dataIndex: 'type',
            key: 'type',
            width: 120
        },
        {
            title: '描述',
            dataIndex: 'description',
            key: 'description',
            width: 120
        },
        {
            title: '尺寸 (宽×高)',
            key: 'size',
            width: 150,
            render: (text: any, record: DeviceType) => `${record.width}×${record.height}`
        },
        {
            title: '标签',
            key: 'tags',
            render: (text: any, record: DeviceType) => (
                <div>
                    {record.tags && record.tags.length > 0 ? (
                        record.tags.map(tag => (
                            <Tag key={tag.id} color="blue" style={{ marginBottom: 4 }}>
                                {tag.remark || tag.tag}
                            </Tag>
                        ))
                    ) : (
                        <span style={{ color: '#999' }}>无标签</span>
                    )}
                </div>
            )
        },
        {
            title: '操作',
            key: 'actions',
            width: 200,
            render: (text: any, record: DeviceType) => (
                <Space>
                    <Tooltip title="编辑">
                        <Button
                            type="primary"
                            size="small"
                            icon={<EditOutlined />}
                            onClick={() => openEditModal(record)}
                        />
                    </Tooltip>
                    <Tooltip title="管理标签">
                        <Button
                            type="default"
                            size="small"
                            icon={<TagsOutlined />}
                            onClick={() => openTagModal(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="确定要删除这个设备类型吗？"
                        onConfirm={() => handleDelete(record)}
                        okText="确定"
                        cancelText="取消"
                    >
                        <Tooltip title="删除">
                            <Button
                                type="primary"
                                danger
                                size="small"
                                icon={<DeleteOutlined />}
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            )
        }
    ];

    // 过滤已添加的标签
    const availableTags = allTags?.filter(tag =>
        !deviceTypeTags?.some(deviceTag => deviceTag.id === tag.id)
    ) || [];

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Title level={2} style={{ margin: 0, display: 'flex', alignItems: 'center' }}>
                        <DesktopOutlined style={{ marginRight: 8 }} />
                        设备类型管理
                    </Title>
                    <Space>
                        <Button
                            type="default"
                            icon={<ReloadOutlined />}
                            onClick={loadDeviceTypes}
                            loading={loading}
                        >
                            刷新
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => setCreateModalOpen(true)}
                        >
                            新增设备类型
                        </Button>
                    </Space>
                </div>

                <Table
                    columns={columns}
                    dataSource={deviceTypes}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
                        onChange: (page, pageSize) => {
                            setPagination(prev => ({
                                ...prev,
                                current: page,
                                pageSize: pageSize || 10
                            }));
                        }
                    }}
                />
            </Card>

            {/* 创建设备类型模态框 */}
            <Modal
                title="新增设备类型"
                open={createModalOpen}
                onCancel={() => {
                    setCreateModalOpen(false);
                    createForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreate}
                >
                    <Form.Item
                        label="设备类型号"
                        name="type"
                        rules={[
                            { required: true, message: '请输入设备类型号' },
                            { type: 'number', min: 1, message: '设备类型号必须是大于0的整数' }
                        ]}
                    >
                        <InputNumber placeholder="请输入设备类型号" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label="描述"
                        name="description"
                        rules={[{ required: true, message: '请输入描述' }]}
                    >
                        <Input placeholder="请输入设备描述" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="宽度"
                                name="width"
                                rules={[
                                    { required: true, message: '请输入宽度' },
                                    { type: 'number', min: 1, message: '宽度必须是大于0的整数' }
                                ]}
                            >
                                <InputNumber placeholder="宽度" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="高度"
                                name="height"
                                rules={[
                                    { required: true, message: '请输入高度' },
                                    { type: 'number', min: 1, message: '高度必须是大于0的整数' }
                                ]}
                            >
                                <InputNumber placeholder="高度" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                创建
                            </Button>
                            <Button onClick={() => {
                                setCreateModalOpen(false);
                                createForm.resetFields();
                            }}>
                                取消
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 编辑设备类型模态框 */}
            <Modal
                title="编辑设备类型"
                open={editModalOpen}
                onCancel={() => {
                    setEditModalOpen(false);
                    setEditingDeviceType(null);
                    editForm.resetFields();
                }}
                footer={null}
                destroyOnClose
            >
                <Form
                    form={editForm}
                    layout="vertical"
                    onFinish={handleEdit}
                >
                    <Form.Item
                        label="设备类型号"
                        name="type"
                        rules={[
                            { required: true, message: '请输入设备类型号' },
                            { type: 'number', min: 1, message: '设备类型号必须是大于0的整数' }
                        ]}
                    >
                        <InputNumber placeholder="请输入设备类型号" style={{ width: '100%' }} />
                    </Form.Item>

                    <Form.Item
                        label="描述"
                        name="description"
                        rules={[{ required: true, message: '请输入描述' }]}
                    >
                        <Input placeholder="请输入设备描述" />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="宽度"
                                name="width"
                                rules={[
                                    { required: true, message: '请输入宽度' },
                                    { type: 'number', min: 1, message: '宽度必须是大于0的整数' }
                                ]}
                            >
                                <InputNumber placeholder="宽度" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="高度"
                                name="height"
                                rules={[
                                    { required: true, message: '请输入高度' },
                                    { type: 'number', min: 1, message: '高度必须是大于0的整数' }
                                ]}
                            >
                                <InputNumber placeholder="高度" style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit">
                                更新
                            </Button>
                            <Button onClick={() => {
                                setEditModalOpen(false);
                                setEditingDeviceType(null);
                                editForm.resetFields();
                            }}>
                                取消
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* 设备类型标签管理模态框 */}
            <Modal
                title={`管理设备类型标签 - ${currentDeviceType?.description}`}
                open={tagModalOpen}
                onCancel={() => {
                    setTagModalOpen(false);
                    setCurrentDeviceType(null);
                    setDeviceTypeTags([]);
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <div>
                    {/* 已添加的标签 */}
                    <div style={{ marginBottom: 20 }}>
                        <Title level={4}>已添加的标签</Title>
                        <div style={{ minHeight: 60, border: '1px dashed #d9d9d9', padding: 10, borderRadius: 4 }}>
                            {deviceTypeTags && deviceTypeTags.length > 0 ? (
                                deviceTypeTags.map(tag => (
                                    <Tag
                                        key={tag.id}
                                        color="blue"
                                        closable
                                        onClose={() => handleRemoveTag(tag.id)}
                                        style={{ marginBottom: 8 }}
                                    >
                                        {tag.remark || tag.tag}
                                    </Tag>
                                ))
                            ) : (
                                <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>
                                    暂无标签
                                </div>
                            )}
                        </div>
                    </div>

                    <Divider />

                    {/* 可添加的标签 */}
                    <div>
                        <Title level={4}>可添加的标签</Title>
                        <div style={{ minHeight: 100 }}>
                            {availableTags.length > 0 ? (
                                availableTags.map(tag => (
                                    <Tag
                                        key={tag.id}
                                        color="default"
                                        style={{
                                            marginBottom: 8,
                                            cursor: 'pointer',
                                            border: '1px dashed #d9d9d9'
                                        }}
                                        onClick={() => handleAddTag(tag.id)}
                                    >
                                        {tag.remark || tag.tag} <PlusOutlined />
                                    </Tag>
                                ))
                            ) : (
                                <div style={{ color: '#999', textAlign: 'center', padding: 20 }}>
                                    所有标签都已添加
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default DeviceTypeManagement; 